import React, { useState, useEffect } from "react";
import { FaEdit, FaEye, FaPlus, FaToggleOn, FaToggleOff, FaSearch, FaFilter } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getBrandsByCompany, updateBrandStatus, getCompanyProfile } from "../../services/companyAuthService";
import Swal from 'sweetalert2'; // Make sure to install sweetalert2

const BrandList = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [brandsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [companyName, setCompanyName] = useState("");

  const navigate = useNavigate();

  // Move fetchData outside useEffect so it can be reused
  const fetchData = async () => {
    try {
      setLoading(true);
      const [brandsResponse, profileResponse] = await Promise.all([
        getBrandsByCompany(),
        getCompanyProfile()
      ]);

      if (brandsResponse.success) {
        setBrands(brandsResponse.brands || []);
      }

      if (profileResponse.success) {
        setCompanyName(profileResponse.company.companyName);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(error.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Use fetchData in useEffect
  useEffect(() => {
    fetchData();
  }, []);

  // Handle status toggle with confirmation
  const handleStatusToggle = async (brandId) => {
    try {
      // Prevent multiple clicks while updating
      if (updatingStatus === brandId) return;

      const brand = brands.find(b => b._id === brandId);
      
      // Only show confirmation for active brands
      if (brand.isActive) {
        // Show confirmation dialog
        const result = await Swal.fire({
          html: `
                   <p>Are you sure Deactivate this brand?</p>
                   <p class="text-red-500 font-medium mt-2">
                     This action is permanent.
                     </p>
                  `,
          // title: 'Confirm Deactivation',
          // text: "Are you sure you want to sdeactivate this brand? It will be permanently deactivated and cannot be activated again.",
          // icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#ef4444', // red color
          cancelButtonColor: '#6b7280',
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
          setUpdatingStatus(brandId);
          const response = await updateBrandStatus(brandId);
          
          if (response.success) {
            toast.success("Brand deactivated successfully");
            fetchData(); // Refresh the brands list
          } else {
            toast.error(response.message || "Failed to deactivate brand");
          }
        }
      } else {
        // Show message that brand cannot be reactivated
        Swal.fire({
          title: 'Cannot Reactivate',
          text: 'This brand has been permanently deactivated and cannot be reactivated.',
          icon: 'error',
          confirmButtonColor: '#ef4444'
        });
      }
    } catch (error) {
      console.error("Error toggling status:", error);
      toast.error(error.message || "Failed to update brand status");
    } finally {
      setUpdatingStatus(null);
    }
  };
  // Handle edit action
  const handleEdit = (brandId) => {
    navigate(`/company/dashboard/brands/edit/${brandId}`);
  };

  // Handle view action
  const handleView = (brandId) => {
    navigate(`/company/dashboard/brands/view/${brandId}`);
  };

  // Filter and sort brands based on search, category, and active status
  const filteredBrands = brands
    .sort((a, b) => {
      // Sort by active status first (active brands come first)
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      // Then sort by creation date within each group (newest first)
      return new Date(b.createdAt) - new Date(a.createdAt);
    })
    .filter(brand => {
      const matchesSearch = brand.brandName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           brand.country.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !filterCategory || brand.category === filterCategory;
      return matchesSearch && matchesCategory;
    });

  // Get unique categories for filter
  const categories = [...new Set(brands.map(brand => brand.category))];

  // Pagination logic
  const indexOfLastBrand = currentPage * brandsPerPage;
  const indexOfFirstBrand = indexOfLastBrand - brandsPerPage;
  const currentBrands = filteredBrands.slice(indexOfFirstBrand, indexOfLastBrand);
  const totalPages = Math.ceil(filteredBrands.length / brandsPerPage);

  if (loading) {
    return (
      <div className="h-[calc(100dvh-80px)] bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
        <p className="text-gray-600 text-xs sm:text-sm md:text-lg">Loading...</p>
      </div>
    </div>
    );
  }

  return (
    <div className="min-h-full">
   
      <div className="max-w-7xl mx-auto px-2 md:px-4 py-4 md:py-8 mt-6">
        {/* Header Section with improved styling */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 ">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
            <div>
              <h1 className="text-lg md:text-xl font-bold text-gray-900 mb-1">Your Brands</h1>
              {companyName && (
                <p className="text-sm text-gray-600">{companyName}</p>
              )}
            </div>
            <Link
              to="/company/dashboard/brands/create"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
            >
              <FaPlus className="mr-2 h-4 w-4" /> Add Brand
            </Link>
          </div>
        </div>

        {/* Show message if no brands with better styling */}
        {!loading && brands.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="max-w-sm mx-auto">
              <p className="text-gray-500 mb-4">No brands found</p>
              <Link
                to="/company/dasboard/brands/create"
                className="text-orange-500 hover:text-orange-600 font-medium transition-colors duration-200"
              >
                Create your first brand
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Filters Section with improved styling */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search brands..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaFilter className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    className="block w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Table Section with improved styling */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Mobile View with improved styling */}
              <div className="block sm:hidden">
                {currentBrands.map((brand) => (
                  <div key={brand._id} 
                    className={`border-b border-gray-200 last:border-b-0 ${
                      !brand.isActive ? 'bg-gray-50' : ''
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className={`font-medium ${
                          !brand.isActive ? 'text-gray-500' : 'text-gray-900'
                        }`}>
                          {brand.brandName}
                        </h3>
                        <button
                          onClick={() => handleStatusToggle(brand._id)}
                          disabled={updatingStatus === brand._id}
                          className={`transition-colors duration-200 ${
                            updatingStatus === brand._id ? 'opacity-50 cursor-not-allowed' :
                            brand.isActive ? "text-green-500 hover:text-green-600" : "text-red-500"
                          }`}
                          title={brand.isActive ? "Deactivate Brand" : "Brand Deactivated"}
                        >
                          {brand.isActive ? 
                            <FaToggleOn size={24} /> : 
                            <FaToggleOff size={24} />
                          }
                        </button>
                      </div>
                      <div className="space-y-2 text-sm">
                        <p className="flex justify-between">
                          <span className="text-gray-500">Company:</span>
                          <span className="font-medium">{brand.companyName}</span>
                        </p>
                        <p className="flex justify-between">
                          <span className="text-gray-500">Country:</span>
                          <span className="font-medium">{brand.country}</span>
                        </p>
                        <p className="flex justify-between">
                          <span className="text-gray-500">Category:</span>
                          <span className="font-medium">{brand.category}</span>
                        </p>
                        <p className="flex justify-between">
                          <span className="text-gray-500">Subcategory:</span>
                          <span className="font-medium">{brand.subcategory}</span>
                        </p>
                      </div>
                      <div className="mt-4 flex justify-end space-x-3">
                        <button 
                          onClick={() => handleEdit(brand._id)}
                          className="inline-flex items-center px-3 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
                        >
                          <FaEdit className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleView(brand._id)}
                          className="inline-flex items-center px-3 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
                        >
                          <FaEye className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop View with improved styling */}
              <div className="hidden sm:block overflow-x-auto">
                <div className="inline-block min-w-full align-middle">
                  <div className="overflow-hidden border-b border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          {/* <th scope="col" className="sticky left-0 z-10 bg-gray-100 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Company Name</th> */}
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Brand Name</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Country</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Category</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Subcategory</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {currentBrands.map((brand) => (
                          <tr key={brand._id} 
                            className={!brand.isActive ? "bg-gray-50" : ""}
                          >
                                {/* <td className="sticky left-0 z-10 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-white">
                                  {brand.companyName}
                                </td> */}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {brand.brandName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {brand.country}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {brand.category}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {brand.subcategory}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleStatusToggle(brand._id)}
                                  disabled={updatingStatus === brand._id}
                                  className={`transition-colors duration-200 ${
                                    updatingStatus === brand._id ? 'opacity-50 cursor-not-allowed' :
                                    brand.isActive ? "text-green-500 hover:text-green-600" : "text-red-500"
                                  }`}
                                  title={brand.isActive ? "Deactivate Brand" : "Brand Deactivated"}
                                >
                                  {brand.isActive ? 
                                    <FaToggleOn size={24} /> : 
                                    <FaToggleOff size={24} />
                                  }
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-3">
                                <button 
                                  onClick={() => handleEdit(brand._id)}
                                  className="text-gray-700 hover:text-green-600 transition-colors duration-200"
                                  title="Edit Brand"
                                >
                                  <FaEdit size={20} />
                                </button>
                                <button 
                                  onClick={() => handleView(brand._id)}
                                  className="text-gray-700 hover:text-blue-600 transition-colors duration-200"
                                  title="View Brand Details"
                                >
                                  <FaEye size={20} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Pagination with improved styling */}
            {totalPages > 1 && (
              <div className="mt-6">
                <nav className="flex justify-center">
                  <ul className="flex items-center space-x-2">
                    {[...Array(totalPages)].map((_, index) => (
                      <li key={index}>
                        <button
                          onClick={() => setCurrentPage(index + 1)}
                          className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200 ${
                            currentPage === index + 1
                              ? "z-10 bg-orange-500 border-orange-500 text-white"
                              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default BrandList;
